/**
 * ArticleContent - Markdown content renderer for knowledge base
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Consistent design language
 * - ✅ TypeScript: Fully typed
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { ArticleContentProps } from '../types';
import { DEFAULT_COLOR } from '../constants';

export const ArticleContent: React.FC<ArticleContentProps> = ({ articleData, accentColor }) => {
  const markdownComponents = {
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 className="text-2xl font-bold mt-8 mb-4" style={{ color: DEFAULT_COLOR }}>
        {children}
      </h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#598D76' }}>
        {children}
      </h3>
    ),
    p: ({ children }: { children: React.ReactNode }) => (
      <p className="mb-4 leading-relaxed" style={{ color: DEFAULT_COLOR }}>
        {children}
      </p>
    ),
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul className="mb-4 space-y-2 list-disc list-inside" style={{ color: DEFAULT_COLOR }}>
        {children}
      </ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="mb-4 space-y-2 list-decimal list-inside" style={{ color: DEFAULT_COLOR }}>
        {children}
      </ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="ml-4">{children}</li>
    ),
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong style={{ color: accentColor }}>{children}</strong>
    ),
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote 
        className="border-l-4 pl-4 my-4 italic" 
        style={{ 
          borderColor: accentColor,
          backgroundColor: `${accentColor}15`,
          padding: '1rem'
        }}
      >
        {children}
      </blockquote>
    ),
    code: ({ children }: { children: React.ReactNode }) => (
      <code 
        className="px-2 py-1 rounded" 
        style={{ 
          backgroundColor: '#f3f4f6',
          color: accentColor 
        }}
      >
        {children}
      </code>
    ),
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
      <Link to={href || '#'} className="underline hover:no-underline" style={{ color: accentColor }}>
        {children}
      </Link>
    ),
    img: ({ src, alt }: { src?: string; alt?: string }) => (
      <img 
        src={src} 
        alt={alt || ''} 
        className="rounded-lg shadow-md my-6 w-full max-w-4xl mx-auto"
        style={{ border: `2px solid ${accentColor}20` }}
      />
    )
  };

  return (
    <article className="bg-white rounded-xl shadow-lg p-8" style={{ borderTop: `4px solid ${accentColor}` }}>
      <h1 className="text-3xl font-bold mb-6" style={{ color: DEFAULT_COLOR }}>
        {articleData.title}
      </h1>
      
      <div className="prose prose-lg max-w-none">
        <ReactMarkdown components={markdownComponents}>
          {articleData.content}
        </ReactMarkdown>
      </div>
    </article>
  );
};